import { OFFICE_TOOLS_GROUP_ID } from 'modules/desktop/shortcutGroups'
import { DesktopGridItem, ShortcutGroup } from 'shared/types/desktop-grid'

import {
  getShortcutGroupBoundaryCollisionItem,
  getShortcutGroupCollisionItems,
  getShortcutGroupPreviewBounds
} from './shortcutGroupBounds'
import { wouldCollide } from './wouldCollide'

type Position = { row: number; col: number }

/**
 * Finds the lowest, then left-most, compact rectangular placement for a group.
 * The members are laid out left-to-right and wrapped into additional rows only
 * when the desktop cannot fit the whole group on one row.
 */
export const getOfficeToolsGroupPlacement = ({
  memberIds,
  items,
  groups,
  cols,
  rows
}: {
  memberIds: string[]
  items: DesktopGridItem[]
  groups: ShortcutGroup[]
  cols: number
  rows: number
}): Map<string, Position> | null => {
  const memberIdSet = new Set(memberIds)
  const members = memberIds
    .map(id => items.find(item => item.id === id))
    .filter((item): item is DesktopGridItem => item !== undefined)
  if (
    members.length !== memberIds.length ||
    members.length === 0 ||
    cols <= 0 ||
    rows <= 0
  ) {
    return null
  }

  const slotWidth = Math.max(...members.map(item => item.size.width))
  const slotHeight = Math.max(...members.map(item => item.size.height))
  const columnsPerRow = Math.min(
    members.length,
    Math.max(1, Math.floor(cols / slotWidth))
  )
  const rowCount = Math.ceil(members.length / columnsPerRow)
  // The grid can scroll vertically, so a short visible viewport must not
  // prevent the group from being provisioned. Reserve one title row as well
  // and search through the expanded logical grid.
  const existingMaxRow = items.reduce(
    (max, item) =>
      memberIdSet.has(item.id)
        ? max
        : Math.max(max, item.position.row + item.size.height),
    rows
  )
  const placementRows = Math.max(
    rows,
    existingMaxRow,
    rowCount * slotHeight + 1
  )
  const maxTopRow = placementRows - rowCount * slotHeight
  const maxLeftCol = cols - columnsPerRow * slotWidth
  if (maxTopRow < 0 || maxLeftCol < 0) return null

  // Remove the members from all obstacle lists, including any old source
  // groups, because this placement is moving them as a single new group.
  const sourceGroupIds = groups
    .filter(group => group.memberIds.some(id => memberIdSet.has(id)))
    .map(group => group.id)
  const obstacles = [
    ...items.filter(item => !memberIdSet.has(item.id)),
    ...getShortcutGroupCollisionItems(groups, items, sourceGroupIds)
  ]

  for (let topRow = maxTopRow; topRow >= 0; topRow -= 1) {
    for (let leftCol = 0; leftCol <= maxLeftCol; leftCol += 1) {
      const positions = members.map((member, index) => ({
        row: topRow + Math.floor(index / columnsPerRow) * slotHeight,
        col: leftCol + (index % columnsPerRow) * slotWidth
      }))
      const previews = members.map((member, index) => ({
        position: positions[index],
        size: member.size
      }))
      const bounds = getShortcutGroupPreviewBounds(previews)
      if (!bounds) continue

      const boundary = getShortcutGroupBoundaryCollisionItem(
        OFFICE_TOOLS_GROUP_ID,
        bounds
      )
      if (
        wouldCollide(boundary.id, boundary.position, boundary.size, obstacles)
      ) {
        continue
      }

      const memberTargets = members.map((member, index) => ({
        ...member,
        position: positions[index]
      }))
      if (
        memberTargets.some(target =>
          wouldCollide(target.id, target.position, target.size, obstacles)
        )
      ) {
        continue
      }

      return new Map(
        memberTargets.map(member => [member.id, member.position] as const)
      )
    }
  }

  return null
}
